import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import type {
  CollectionHierarchyQuery,
  CollectionNode,
  ApiResponse,
} from "src/types/collection/collectionTypes";
import { axiosInstance } from "src/api/axiosInstance";
import type { ServerPageContent } from "src/types/collection/collectionTypes";
import { normalizeCollectionPage } from "src/store/collection/collection.mappers";
import { Pagination, Stack } from "@mui/material";
import CustomButton from "src/components/common/CustomButton";

const PARENT_SEARCH_URL = "/v1/admin/baseinfo/collection/tree";

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (node: CollectionNode) => void;
  disabledIds?: number[];
};

const defaultQuery: CollectionHierarchyQuery = {
  collectionName: "",
  parentCollectionId: undefined,
  depth: undefined,
  isActive: undefined,
  isFeatured: undefined,
  page: 0,
  size: 10,
  order: "displayOrder,asc",
};

type VisibleRow = {
  node: CollectionNode;
  level: number;
  hasChildren: boolean;
  isExpanded: boolean;
  childrenCount: number;
};

function buildVisibleRows(roots: CollectionNode[], expanded: Record<number, boolean>) {
  const rows: VisibleRow[] = [];

  const walk = (nodes: CollectionNode[], level: number) => {
    for (const n of nodes) {
      const children = n.children ?? [];
      const hasChildren = children.length > 0;
      const isExpanded = !!expanded[n.collectionId];

      rows.push({
        node: n,
        level,
        hasChildren,
        isExpanded,
        childrenCount: children.length,
      });

      if (hasChildren && isExpanded) {
        walk(children, level + 1);
      }
    }
  };

  walk(roots, 0);
  return rows;
}

export default function CollectionParentPickerModal({
  open,
  onClose,
  onSelect,
  disabledIds = [],
}: Props) {
  const [query, setQuery] = useState<CollectionHierarchyQuery>(defaultQuery);
  const [inputName, setInputName] = useState("");

  const [roots, setRoots] = useState<CollectionNode[]>([]);
  const [pageMeta, setPageMeta] = useState<{
    page: number;
    size: number;
    totalPages: number;
    totalElements: number;
  } | null>(null);

  const [expandedIds, setExpandedIds] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const disabledSet = useMemo(() => new Set(disabledIds), [disabledIds]);

  const rows = useMemo(() => buildVisibleRows(roots, expandedIds), [roots, expandedIds]);

  const totalPages = pageMeta?.totalPages ?? 1;
  const totalElements = pageMeta?.totalElements ?? 0;

  const toggleExpand = (id: number) => {
    setExpandedIds((m) => ({ ...m, [id]: !m[id] }));
  };

  const expandAll = () => {
    const next: Record<number, boolean> = {};
    const walk = (nodes: CollectionNode[]) => {
      nodes.forEach((n) => {
        if (n.children?.length) next[n.collectionId] = true;
        if (n.children?.length) walk(n.children);
      });
    };
    walk(roots);
    setExpandedIds(next);
  };

  const collapseAll = () => setExpandedIds({});

  const fetch = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await axiosInstance.get<ApiResponse<ServerPageContent<CollectionNode>>>(
        PARENT_SEARCH_URL,
        {
          params: {
            ...query,
            collectionName: query.collectionName?.trim() || undefined,
            order: query.order || "displayOrder,asc",
          },
        }
      );

      const ok =
        res.data?.statusCode === 1 || res.data?.statusCode === 0 || res.data?.statusCode == null;
      if (!ok) throw new Error(res.data?.message || "부모 컬렉션 목록 조회 실패");

      // ✅ 서버 page 구조 → UI 표준 PageContent로 정규화
      const normalized = normalizeCollectionPage(res.data.data);

      setRoots(normalized.content || []);
      setPageMeta({
        page: normalized.page,
        size: normalized.size,
        totalPages: normalized.totalPages,
        totalElements: normalized.totalElements,
      });

      // 검색/페이지 바뀌면 펼침 상태 초기화(원하면 유지해도 됨)
      setExpandedIds({});
    } catch (e: any) {
      setError(e?.message || "조회 중 오류가 발생했습니다.");
      setRoots([]);
      setPageMeta(null);
      setExpandedIds({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    query.page,
    query.size,
    query.order,
    query.isActive,
    query.isFeatured,
    query.depth,
    query.parentCollectionId,
    query.collectionName,
    open,
  ]);

  if (!open) return null;

  const onSearch = () => {
    setQuery((q) => ({ ...q, collectionName: inputName, page: 0 }));
  };

  const onReset = () => {
    setInputName("");
    setQuery({ ...defaultQuery });
  };

  return (
    <Dim onMouseDown={onClose}>
      <Modal onMouseDown={(e) => e.stopPropagation()}>
        <Top>
          <Title>부모 컬렉션 선택</Title>
          <TopRight>
            <CustomButton onClick={expandAll} disabled={loading}>
              전체 펼치기
            </CustomButton>
            <CustomButton onClick={collapseAll} disabled={loading}>
              전체 접기
            </CustomButton>
            <XBtn type="button" onClick={onClose}>
              ✕
            </XBtn>
          </TopRight>
        </Top>

        <Body>
          <FilterBar>
            <Input
              value={inputName}
              onChange={(e) => setInputName(e.target.value)}
              placeholder="컬렉션명 검색"
              onKeyDown={(e) => {
                if (e.key === "Enter") onSearch();
              }}
            />
            <Select
              value={String(query.size)}
              onChange={(e) => setQuery((q) => ({ ...q, size: Number(e.target.value), page: 0 }))}
            >
              <option value="10">10개</option>
              <option value="20">20개</option>
              <option value="50">50개</option>
            </Select>

            <CustomButton onClick={onSearch} disabled={loading}>
              검색
            </CustomButton>
            <CustomButton onClick={onReset} disabled={loading}>
              초기화
            </CustomButton>
          </FilterBar>

          <MetaRow>
            <Meta>총 {totalElements}건</Meta>
            {loading ? <Meta>불러오는 중...</Meta> : null}
          </MetaRow>

          {error ? <ErrorBox>{error}</ErrorBox> : null}

          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th style={{ width: 120 }}>컬렉션 ID</th>
                  <th>컬렉션명</th>
                  <th style={{ width: 120 }}>선택</th>
                </tr>
              </thead>
              <tbody>
                {!loading && rows.length === 0 ? (
                  <tr>
                    <td colSpan={3}>
                      <Empty>데이터가 없습니다.</Empty>
                    </td>
                  </tr>
                ) : null}

                {rows.map((r) => {
                  const node = r.node;
                  const disabled = disabledSet.has(node.collectionId);

                  return (
                    <tr key={node.collectionId}>
                      <td>{node.collectionId}</td>
                      <td>
                        <NameCell>
                          <Indent style={{ width: r.level * 16 }} />
                          {r.hasChildren ? (
                            <ExpandBtn
                              type="button"
                              onClick={() => toggleExpand(node.collectionId)}
                            >
                              {r.isExpanded ? "▾" : "▸"}
                            </ExpandBtn>
                          ) : (
                            <ExpandPlaceholder />
                          )}

                          <Name title={node.collectionName}>{node.collectionName}</Name>
                          {r.hasChildren ? <ChildCount>({r.childrenCount})</ChildCount> : null}
                        </NameCell>
                      </td>
                      <td>
                        <PrimaryBtn
                          type="button"
                          disabled={disabled}
                          onClick={() => {
                            onSelect(node);
                            onClose();
                          }}
                        >
                          선택
                        </PrimaryBtn>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>

          <Stack direction="row" mt={2} spacing={2} alignItems="center" justifyContent="center">
            <Pagination
              page={(query.page ?? 0) + 1} // ✅ MUI는 1-based
              count={Math.max(1, totalPages)}
              disabled={loading}
              showFirstButton
              showLastButton
              onChange={(_: React.ChangeEvent<unknown>, page: number) => {
                setQuery((q) => ({
                  ...q,
                  page: page - 1, // ✅ 다시 0-based로 변환
                }));
              }}
            />
          </Stack>
        </Body>

        <Bottom>
          <GhostBtn type="button" onClick={onClose}>
            닫기
          </GhostBtn>
        </Bottom>
      </Modal>
    </Dim>
  );
}

/* styles */

const Dim = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const Modal = styled.div`
  width: 720px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 32px);
  overflow: auto;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
`;

const Top = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TopRight = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Title = styled.div`
  font-size: 15px;
  font-weight: 900;
`;

const XBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid #eee;
  background: #fff;
  cursor: pointer;
`;

const Body = styled.div`
  padding: 14px 16px 16px;
`;

const FilterBar = styled.div`
  display: grid;
  grid-template-columns: 1fr 120px 90px 90px;
  gap: 10px;
  align-items: center;
`;

const Input = styled.input`
  height: 38px;
  padding: 0 10px;
  border: 1px solid #ddd;
  border-radius: 10px;
`;

const Select = styled.select`
  height: 38px;
  padding: 0 10px;
  border: 1px solid #ddd;
  border-radius: 10px;
  background: #fff;
`;

const MetaRow = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
`;

const Meta = styled.div`
  font-size: 12px;
  color: #666;
`;

const TableWrap = styled.div`
  margin-top: 10px;
  border: 1px solid #eee;
  border-radius: 12px;
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    border-bottom: 1px solid #f2f2f2;
    padding: 10px 12px;
    font-size: 13px;
    text-align: left;
    white-space: nowrap;
  }

  th {
    background: #fafafa;
    font-weight: 800;
    /* 헤더 좌우정렬 통일 (가운데) */
    text-align: center;
  }
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Indent = styled.div`
  flex: 0 0 auto;
`;

const ExpandBtn = styled.button`
  width: 26px;
  height: 26px;
  border-radius: 8px;
  border: 1px solid #ddd;
  background: #fff;
  cursor: pointer;
`;

const ExpandPlaceholder = styled.div`
  width: 26px;
  height: 26px;
`;

const Name = styled.div`
  max-width: 380px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChildCount = styled.div`
  font-size: 12px;
  color: #888;
`;

const Empty = styled.div`
  padding: 22px;
  text-align: center;
  color: #777;
`;

const ErrorBox = styled.div`
  margin-top: 10px;
  padding: 10px 12px;
  border: 1px solid #ffd7d7;
  background: #fff3f3;
  color: #b00000;
  border-radius: 10px;
`;

const Bottom = styled.div`
  padding: 12px 16px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
`;

const Btn = styled.button`
  height: 36px;
  padding: 0 12px;
  border-radius: 10px;
  cursor: pointer;
  border: 1px solid #ddd;
  background: #fff;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const GhostBtn = styled(Btn)``;

const PrimaryBtn = styled(Btn)`
  background: #111;
  color: #fff;
  border: 0;
`;
